import * as PI from '@sosocio/creator-types/project';
import analytics from 'controllers/analytics';
import * as DialogService from 'services/dialog';
import {
	isLimitedColorLogoPhotoError,
	isLogoTooComplexError,
	openLimitedColorLogoPhotoErrorDialog,
	openLogoTooComplexErrorDialog,
} from 'services/photo-processing-error-dialog';
import { canUseDesignPrintLocally } from 'services/upload/design-print-local-preview';
import {
	MIME_TYPES_EPS,
	MIME_TYPES_HEIC,
	MIME_TYPES_PDF,
	MIME_TYPES_RASTER_IMAGES,
	MIME_TYPES_REQUIRE_CONVERSION,
	MIME_TYPES_WITH_EMBEDDED_IMAGES,
} from 'settings/filetypes';
import {
	AppStateModule,
	PhotosModule,
	ProductStateModule,
	UIModule,
	UploadModule,
} from 'store';
import { componentSlot } from 'store/ui';
import type { Component } from 'vue';
import type { Cons } from 'vue-facing-decorator/dist/component';

export type FileConversionWaitOptions = {
	vectorizationVariant?: 'analyzing' | 'optimizing';
};

export type FileConversionDialogComponents = {
	fileConversion: Cons;
	vectorization: Component;
};

type FileConversionDialogDependencies = {
	dialogs: FileConversionDialogComponents;
	isLocalPreviewUnusable: (localRef: string) => boolean;
};

type QueuedProcessingWaitOptions = {
	handleLogoErrors: boolean;
	variant: 'analyzing' | 'optimizing' | 'preparing-file';
};

const vectorizationDialogTimeoutMs = 120000;
const fileConversionDialogTimeoutMs = 120000;

/**
 * Resolve design-print uploads that cannot be rendered from their local preview.
 *
 * Browser-renderable raster originals and native bridge previews stay interactive
 * while processing continues. Other formats need blocking progress until a remote
 * raster becomes available.
 *
 * @param isLocalPreviewUnusable Checks whether inspection rejected a local preview.
 * @returns Queued design-print models that require processing progress.
 */
function getDesignPrintsToProcess(
	isLocalPreviewUnusable: FileConversionDialogDependencies['isLocalPreviewUnusable'],
): PI.PhotoModel[] {
	return ProductStateModule.getPhotosQueued.filter((photoModel) => (
		photoModel._vectorize?.isDesignPrint
		&& !canUseDesignPrintLocally(
			photoModel,
			isLocalPreviewUnusable,
		)
	));
}

/**
 * Resolve queued uploads that will actually enter the vectorizer.
 *
 * Design prints carry the same transport options object, but their explicit
 * marker routes them around vectorization in process-photo.
 *
 * @returns Queued {@link PI.PhotoModel} entries that require vectorization progress.
 */
function getPhotosToVectorize(): PI.PhotoModel[] {
	return ProductStateModule.getPhotosQueued.filter((photoModel) => (
		photoModel._vectorize
		&& !photoModel._vectorize.isDesignPrint
		&& (
			!photoModel._type
			|| MIME_TYPES_RASTER_IMAGES.includes(photoModel._type)
			|| (
				ProductStateModule.getOffering?.type === 'logo'
				&& MIME_TYPES_WITH_EMBEDDED_IMAGES.includes(photoModel._type)
			)
		)
	));
}

/**
 * Resolve queued non-design uploads that require general file conversion.
 *
 * Design prints are handled by their dedicated processing branch so locally
 * usable native previews do not re-enter conversion solely because MIME metadata
 * is absent.
 *
 * @returns Queued non-design models requiring general file conversion.
 */
function getPhotosToConvert(): PI.PhotoModel[] {
	return ProductStateModule.getPhotosQueued.filter((photoModel) => {
		if (photoModel._vectorize?.isDesignPrint) {
			return false;
		}

		if (!photoModel._type) {
			return true;
		}

		return MIME_TYPES_REQUIRE_CONVERSION.includes(photoModel._type);
	});
}

/**
 * Resolve the latest store model for a queued processing snapshot.
 *
 * @param photoModel Queue snapshot to resolve by local reference or id.
 * @returns Latest photo model when it is available in the store.
 */
function resolveQueuedProcessingModel(photoModel: PI.PhotoModel): PI.PhotoModel | undefined {
	if (photoModel._localRef) {
		const localRefModel = PhotosModule.findWhere({
			_localRef: photoModel._localRef,
		});

		if (localRefModel) {
			return localRefModel;
		}
	}

	return PhotosModule.getById(photoModel.id);
}

/**
 * Resolve every failed model represented by a processing queue snapshot.
 *
 * The service owns settlement polling, so it must also own error dispatch. If
 * error handling were left exclusively to the progress component, this poll
 * could close that component before its matching timer emits the error event.
 *
 * @param imagesToProcess Queue snapshots being monitored.
 * @returns Latest failed models, de-duplicated by their resolved model object.
 */
function getQueuedProcessingErrors(imagesToProcess: PI.PhotoModel[]): PI.PhotoModel[] {
	return imagesToProcess.reduce<PI.PhotoModel[]>(
		(errors, photoModel) => {
			const queuedModel = resolveQueuedProcessingModel(photoModel);

			if (
				queuedModel?._error
				&& !errors.includes(queuedModel)
			) {
				errors.push(queuedModel);
			}

			return errors;
		},
		[],
	);
}

/**
 * Determine whether every queued model has either completed or failed.
 *
 * @param imagesToProcess Queue snapshots being monitored.
 * @returns True when every corresponding store model is in a terminal state.
 */
function areQueuedProcessesSettled(imagesToProcess: PI.PhotoModel[]): boolean {
	return imagesToProcess.every((photoModel) => {
		const queuedModel = resolveQueuedProcessingModel(photoModel);

		if (!queuedModel) {
			/*
			 * The queued model cannot be resolved yet. This happens during a
			 * transient lookup miss while the model is still being created, so
			 * treat it as NOT settled. Resolving it as settled here would skip
			 * processing flow the moment the synchronous initial check runs; the
			 * 120s timeout still bounds a model that genuinely never re-appears.
			 */
			return false;
		}

		if (queuedModel._error) {
			// A failed processing request is terminal; its owning upload flow handles the error.
			return true;
		}

		return typeof queuedModel.id !== 'string' && Boolean(queuedModel.url);
	});
}

/**
 * Show the modern processing progress dialog until queued remote assets settle.
 *
 * Logo vectorization retains its specialized analytics and error dialogs. The
 * design-print path reuses only the progress shell and readiness polling.
 *
 * @param imagesToProcess Queue snapshots to monitor.
 * @param options Dialog copy and logo-error behavior.
 * @param dependencies Injected dialog components.
 * @returns Settlement status when processing completes or reaches its timeout.
 */
function waitForQueuedProcessing(
	imagesToProcess: PI.PhotoModel[],
	options: QueuedProcessingWaitOptions,
	dependencies: FileConversionDialogDependencies,
): Promise<'settled' | 'timeout'> {
	if (options.handleLogoErrors) {
		analytics.trackEvent(
			'Vectorization needed',
			{
				groupid: ProductStateModule.getProduct?.group,
			},
		);
	}

	return new Promise((resolve) => {
		let resolved = false;
		let settleCheckInterval: number | null = null;
		let settleTimeout: number | null = null;
		const handledProcessingErrors = new Set<PI.PhotoModel>();

		const stopSettleCheckInterval = (): void => {
			if (settleCheckInterval !== null) {
				window.clearInterval(settleCheckInterval);
				settleCheckInterval = null;
			}
		};

		const stopSettleTimeout = (): void => {
			if (settleTimeout !== null) {
				window.clearTimeout(settleTimeout);
				settleTimeout = null;
			}
		};

		const resolveOnce = (status: 'settled' | 'timeout'): void => {
			if (resolved) {
				return;
			}

			resolved = true;
			resolve(status);
			stopSettleCheckInterval();
			stopSettleTimeout();
			closeDialog();
		};

		const handleProcessingError = (photoModel: PI.PhotoModel): void => {
			if (
				!options.handleLogoErrors
				|| handledProcessingErrors.has(photoModel)
			) {
				return;
			}

			handledProcessingErrors.add(photoModel);

			if (isLimitedColorLogoPhotoError(photoModel)) {
				openLimitedColorLogoPhotoErrorDialog();
			} else if (isLogoTooComplexError(photoModel)) {
				openLogoTooComplexErrorDialog(
					photoModel._error.code,
				);
			}
		};

		const resolveWhenSettled = (): void => {
			getQueuedProcessingErrors(imagesToProcess).forEach(handleProcessingError);

			if (areQueuedProcessesSettled(imagesToProcess)) {
				resolveOnce('settled');
			}
		};

		const handleProcessingComponentError = (photoModel: PI.PhotoModel): void => {
			handleProcessingError(photoModel);
			resolveWhenSettled();
		};

		const closeDialog = UIModule.openDialog({
			body: componentSlot({
				tag: dependencies.dialogs.vectorization,
				props: {
					models: imagesToProcess,
					variant: options.variant,
					/*
					 * The progress view only emits `closeDialog` once it has
					 * polled the models to completion (it has no user close button),
					 * so this guard is defensive: a close event here always means the
					 * jobs are settled. This intentionally differs from the conversion
					 * dialog's listener, which resolves unconditionally because the
					 * conversion view emits `closeDialog` only at 100% progress. Both
					 * close events are terminal; do not relax this to an unconditional
					 * resolve, which could settle before processing finishes.
					 */
					onCloseDialog: resolveWhenSettled,
					'onVectorization-error': handleProcessingComponentError,
				},
			}),
			props: {
				class: 'md:w-130',
				hasCloseButton: false,
			},
		});

		settleCheckInterval = window.setInterval(
			resolveWhenSettled,
			120,
		);
		settleTimeout = window.setTimeout(
			() => {
				resolveOnce('timeout');
			},
			vectorizationDialogTimeoutMs,
		);
		resolveWhenSettled();
	});
}

function trackFileConversionAnalytics(imagesToConvert: PI.PhotoModel[]): void {
	analytics.trackEvent(
		'File conversion needed',
		{
			groupid: ProductStateModule.getProduct?.group,
			heic: imagesToConvert.filter(
				(photoModel) => photoModel._type
					&& MIME_TYPES_HEIC.includes(photoModel._type),
			).length,
			vectors: imagesToConvert.filter(
				(photoModel) => photoModel._type
					&& MIME_TYPES_EPS.includes(photoModel._type),
			).length,
			pdf: imagesToConvert.filter(
				(photoModel) => photoModel._type
					&& MIME_TYPES_PDF.includes(photoModel._type),
			).length,
		},
	);
}

function waitForConversion(
	imagesToConvert: PI.PhotoModel[],
	dependencies: FileConversionDialogDependencies,
): Promise<'settled' | 'timeout'> {
	return new Promise((resolve) => {
		let resolved = false;
		let interval: number | null = null;
		let timeout: number | null = null;
		let closeDialog: (() => void) | undefined = undefined;

		const resolveOnce = (status: 'settled' | 'timeout'): void => {
			if (resolved) {
				return;
			}

			resolved = true;

			if (interval !== null) {
				window.clearInterval(interval);
				interval = null;
			}

			if (timeout !== null) {
				window.clearTimeout(timeout);
				timeout = null;
			}

			resolve(status);
			closeDialog?.();
		};

		const opened = DialogService.openDialog({
			header: {
				hasCloseButton: false,
			},
			body: {
				component: dependencies.dialogs.fileConversion,
				props: {
					offeringType: ProductStateModule.getOffering?.type || 'photo',
					photosProcessed: [],
					progressPercentage: 0,
					submittedCount: imagesToConvert.length,
				},
				listeners: {
					closeDialog: () => {
						resolveOnce('settled');
					},
				},
			},
			width: 600,
		});

		closeDialog = opened.close;
		const apiDialog = opened.api;

		timeout = window.setTimeout(
			() => {
				resolveOnce('timeout');
			},
			fileConversionDialogTimeoutMs,
		);

		interval = window.setInterval(
			() => {
				const uploadProgress = UploadModule.totalBytesSpecial > 0
					? UploadModule.totalUploadBytesSpecial / UploadModule.totalBytesSpecial
					: 1;

				const photosProcessed: PI.PhotoModel[] = [];
				const photosErrors: PI.PhotoModel[] = [];

				imagesToConvert.forEach((photoModelToConvert) => {
					const photoModel = PhotosModule.findWhere({
						_localRef: photoModelToConvert._localRef,
					});

					if (photoModel && typeof photoModel.id !== 'string') {
						photosProcessed.push(photoModel);
					} else if (photoModel?._error) {
						photosErrors.push(photoModel);
					}
				});

				const saveProgress = photosProcessed.length + photosErrors.length
					? (photosProcessed.length + photosErrors.length) / imagesToConvert.length
					: 0;

				const fileConversionComponent = apiDialog.bodyComponent();

				if (!fileConversionComponent) {
					/*
					 * The dialog was destroyed by other means; there is no component
					 * left to update. Resolve as settled so the flow is not left
					 * blocked waiting on a dialog that no longer exists.
					 */
					resolveOnce('settled');

					return;
				}

				if (UploadModule.totalBytesSpecial) {
					fileConversionComponent.progressPercentage = Math.floor(
						100 * (0.75 * uploadProgress + 0.25 * saveProgress),
					);
				} else {
					fileConversionComponent.progressPercentage = Math.floor(100 * saveProgress);
				}

				if (fileConversionComponent.progressPercentage >= 100) {
					/*
					 * Conversion progress is complete. Resolve directly here so the
					 * flow does not depend on the injected dialog component emitting
					 * `closeDialog`, mirroring how the vectorization path settles by
					 * polling. resolveOnce also clears the interval and timeout.
					 */
					resolveOnce('settled');
				}
			},
			100,
		);
	});
}

export function waitForFileConversion(
	options: FileConversionWaitOptions = {},
	dependencies: FileConversionDialogDependencies,
): Promise<'settled' | 'timeout'> {
	const designPrintsToProcess = getDesignPrintsToProcess(
		dependencies.isLocalPreviewUnusable,
	);

	if (designPrintsToProcess.length > 0) {
		trackFileConversionAnalytics(designPrintsToProcess);

		if (AppStateModule.uploadOnly) {
			return Promise.resolve('settled');
		}

		return waitForQueuedProcessing(
			designPrintsToProcess,
			{
				handleLogoErrors: false,
				variant: 'preparing-file',
			},
			dependencies,
		);
	}

	const imagesToVectorize = getPhotosToVectorize();

	if (imagesToVectorize.length > 0) {
		return waitForQueuedProcessing(
			imagesToVectorize,
			{
				handleLogoErrors: true,
				variant: options.vectorizationVariant || 'optimizing',
			},
			dependencies,
		);
	}

	const imagesToConvert = getPhotosToConvert();

	if (imagesToConvert.length) {
		trackFileConversionAnalytics(imagesToConvert);
	}

	if (imagesToConvert.length === 0 || AppStateModule.uploadOnly) {
		return Promise.resolve('settled');
	}

	return waitForConversion(
		imagesToConvert,
		dependencies,
	);
}
